import type { QueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import type { RealEstate } from '../../models/apiTypes';
import { MapObject, normalizeMapObject } from '../../models/mapObject';
import type { UserData } from '../../models/user';
import { buyMapObjectMapObjectsMapObjectIdBuyPost } from '../../client';
import { handleApiError } from '../../utils/net/apiError';
import { showAlert } from '../../utils/telegram/alert';
import type { InsufficientCurrencyError } from '../modals/InsufficientPearlsModal';

export interface RealEstateQuotaExceededError {
  error_type: "real_estate_quota_exceeded";
  current_real_estate_count: number;
  message: string;
}

export function isInvoiceLinkResponse(data: unknown): data is { invoice_link: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'invoice_link' in data &&
    typeof (data as { invoice_link: unknown }).invoice_link === 'string'
  );
}

export async function buyMapObjectViaClient(
  plotId: number,
  realEstateId: number,
  paymentMethod: 'pearls' | 'stars',
) {
  const { data } = await buyMapObjectMapObjectsMapObjectIdBuyPost({
    path: { map_object_id: plotId },
    body: {
      real_estate_id: realEstateId,
      payment_method: paymentMethod,
    },
    throwOnError: true,
  });
  return data;
}

/** Усе, що обробники покупки читали із closure компонента. */
export interface RealEstatePurchaseDeps {
  plotId: number | null;
  realEstates: RealEstate[];
  queryClient: QueryClient;
  t: (key: string, options?: Record<string, unknown>) => string;
  getRealEstateNameLocal: (id: number) => string;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
  setInsufficientCurrencyError: (value: InsufficientCurrencyError | null) => void;
  setQuotaExceededError: (value: RealEstateQuotaExceededError | null) => void;
  setErrorRealEstateId: (value: number | null) => void;
  setShowConfetti: (value: boolean) => void;
  setPurchasedRealEstateId: (value: number | null) => void;
  setShowPurchaseSuccessModal: (value: boolean) => void;
  onClose: () => void;
}

export function handlePurchaseError(
  deps: RealEstatePurchaseDeps,
  error: unknown,
  realEstateId: number,
  currency: 'pearls' | 'stars',
  selectedEstateMeta: RealEstate | undefined,
): boolean {
  const {
    plotId, realEstates, queryClient, t, getRealEstateNameLocal,
    setInsufficientCurrencyError, setQuotaExceededError, setErrorRealEstateId,
  } = deps;

  console.error('[RealEstateModal] Purchase error:', error);
  const err = handleApiError(error, { logLabel: 'buyHouse' });

  if (err.errorType === 'insufficient_currency') {
    const userData = queryClient.getQueryData<UserData>(['userData']);
    posthog.capture('real_estate_insufficient_pearls', {
      property_id: realEstateId,
      property_type: getRealEstateNameLocal(realEstateId),
      currency,
      required_pearls: err.extra.required_pearls ?? selectedEstateMeta?.price_pearls ?? null,
      available_pearls: err.extra.available_pearls ?? userData?.currency_balance ?? null,
      plot_id: plotId,
    });
    setInsufficientCurrencyError({
      error_type: 'insufficient_currency',
      required_pearls: err.extra.required_pearls ?? null,
      available_pearls: err.extra.available_pearls ?? null,
      required_stars: err.extra.required_stars ?? null,
      available_stars: err.extra.available_stars ?? null,
    });
    setErrorRealEstateId(realEstateId);
    return true;
  }

  if (err.errorType === 'real_estate_sold_out') {
    posthog.capture('real_estate_sold_out', {
      property_id: realEstateId,
      property_type: getRealEstateNameLocal(realEstateId),
      currency,
    });
    queryClient.invalidateQueries({ queryKey: ['realEstates'] });
    showAlert(t('This house is sold out. Please choose another one.'));
    return true;
  }

  if (err.errorType === 'conflict' || err.errorType === 'bad_request') {
    if (err.detail.includes('already owned')) {
      posthog.capture('real_estate_plot_taken', {
        property_id: realEstateId,
        property_type: getRealEstateNameLocal(realEstateId),
        currency,
        plot_id: plotId,
      });
      queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
      showAlert(t('This plot has already been taken by another player. Please choose a different one.'));
      return true;
    }
    if (err.detail.includes('already has a building')) {
      posthog.capture('real_estate_plot_occupied', {
        property_id: realEstateId,
        property_type: getRealEstateNameLocal(realEstateId),
        currency,
        plot_id: plotId,
      });
      queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
      showAlert(t('This plot already has a building on it.'));
      return true;
    }
  }

  if (err.errorType === 'real_estate_quota_exceeded' && currency === 'pearls') {
    const { extra } = err;
    let shouldShowInsufficientCurrency = false;

    const userData = queryClient.getQueryData<UserData>(['userData']);
    const selectedEstate = realEstates.find((estate) => estate.id === realEstateId);
    if (userData && selectedEstate && selectedEstate.price_pearls != null) {
      const userBalance = userData.currency_balance ?? 0;
      const requiredPrice = selectedEstate.price_pearls ?? 0;
      if (userBalance < requiredPrice) {
        shouldShowInsufficientCurrency = true;
      }
    }

    if (shouldShowInsufficientCurrency) {
      posthog.capture('real_estate_insufficient_pearls', {
        property_id: realEstateId,
        property_type: getRealEstateNameLocal(realEstateId),
        currency: 'pearls',
        required_pearls: selectedEstate?.price_pearls ?? null,
        available_pearls: userData?.currency_balance ?? null,
        plot_id: plotId,
      });
      setInsufficientCurrencyError({
        error_type: 'insufficient_currency',
        required_pearls: selectedEstate?.price_pearls ?? null,
        available_pearls: userData?.currency_balance ?? null,
        required_stars: selectedEstate?.price_stars ?? null,
        available_stars: null,
      });
      setErrorRealEstateId(realEstateId);
      return true;
    }

    posthog.capture('real_estate_quota_exceeded', {
      property_id: realEstateId,
      property_type: getRealEstateNameLocal(realEstateId),
      currency: 'pearls',
      current_count: extra.current_real_estate_count ?? null,
    });
    setQuotaExceededError({
      error_type: 'real_estate_quota_exceeded',
      current_real_estate_count: extra.current_real_estate_count,
      message: err.detail,
    });
    setErrorRealEstateId(realEstateId);
    return true;
  }

  return false;
}

// Обробник оплати через ігрову валюту
export async function handleBuyWithGameCurrency(
  deps: RealEstatePurchaseDeps,
  realEstateId: number,
): Promise<void> {
  const {
    plotId, realEstates, queryClient, t, getRealEstateNameLocal,
    isProcessing, setIsProcessing, setShowConfetti,
    setPurchasedRealEstateId, setShowPurchaseSuccessModal,
  } = deps;

  if (isProcessing) return;
  if (plotId == null) return;

  const selectedEstateMeta = realEstates.find((e) => e.id === realEstateId);
  posthog.capture('real_estate_buy_attempted', {
    property_id: realEstateId,
    property_type: getRealEstateNameLocal(realEstateId),
    currency: 'pearls',
    price: selectedEstateMeta?.price_pearls ?? null,
    plot_id: plotId,
  });

  setIsProcessing(true);
  try {
    const response = await buyMapObjectViaClient(plotId, realEstateId, 'pearls');

    if (isInvoiceLinkResponse(response)) {
      throw new Error('Unexpected invoice link response for pearls payment');
    }

    const selectedEstate = realEstates.find((estate) => estate.id === realEstateId);
    posthog.capture('real_estate_purchased', {
      property_type: getRealEstateNameLocal(realEstateId),
      property_id: realEstateId,
      currency: 'pearls',
      price: selectedEstate?.price_pearls ?? null,
    });

    queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) => {
      return oldData.map((obj) =>
        obj.id === plotId
          ? normalizeMapObject(response.map_object)
          : obj
      );
    });

    queryClient.invalidateQueries({ queryKey: ['userData'] });
    queryClient.invalidateQueries({ queryKey: ['realEstates'] });

    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 5000);

    setPurchasedRealEstateId(realEstateId);
    setShowPurchaseSuccessModal(true);
  } catch (error) {
    if (!handlePurchaseError(deps, error, realEstateId, 'pearls', selectedEstateMeta)) {
      posthog.capture('real_estate_purchase_error', {
        property_id: realEstateId,
        property_type: getRealEstateNameLocal(realEstateId),
        currency: 'pearls',
        error_message: error instanceof Error ? error.message : 'unknown',
        plot_id: plotId,
      });
      showAlert(t('Purchase failed. Please try again.'));
    }
  } finally {
    setIsProcessing(false);
  }
}

// Обробник оплати через Telegram Stars
export async function handleBuyWithStars(
  deps: RealEstatePurchaseDeps,
  realEstateId: number,
): Promise<void> {
  const {
    plotId, realEstates, queryClient, t, getRealEstateNameLocal,
    isProcessing, setIsProcessing, setShowConfetti,
    setPurchasedRealEstateId, setShowPurchaseSuccessModal, onClose,
  } = deps;

  if (isProcessing) return;
  if (plotId == null) return;

  const selectedEstateMeta = realEstates.find((e) => e.id === realEstateId);
  posthog.capture('real_estate_buy_attempted', {
    property_id: realEstateId,
    property_type: getRealEstateNameLocal(realEstateId),
    currency: 'stars',
    price: selectedEstateMeta?.price_stars ?? null,
    plot_id: plotId,
  });

  setIsProcessing(true);
  try {
    const response = await buyMapObjectViaClient(plotId, realEstateId, 'stars');

    if (!isInvoiceLinkResponse(response)) {
      throw new Error('Unexpected response format for stars payment');
    }

    const invoiceLink = response.invoice_link;

    // Викликаємо Telegram WebApp API для відкриття invoice
    const tg = window.Telegram?.WebApp;
    if (!tg) {
      throw new Error(t("Telegram WebApp is not available"));
    }

    // Використовуємо openInvoice для відкриття invoice по посиланню
    tg.openInvoice(invoiceLink, (status: string) => {
      if (status === 'paid') {
        const selectedEstate = realEstates.find((estate) => estate.id === realEstateId);
        posthog.capture('real_estate_purchased', {
          property_type: getRealEstateNameLocal(realEstateId),
          property_id: realEstateId,
          currency: 'stars',
          price: selectedEstate?.price_stars ?? null,
        });

        // Показуємо конфеті для ВСІХ покупок будинків
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);

        // Zoom до будинку після інструкційного попапу (див. handleGoToHouse), не тут.

        // Оновлюємо дані після успішної оплати
        queryClient.invalidateQueries({ queryKey: ['userData'] });

        // Інвалідуємо кеш real estate каталогу (може змінитися purchased_count, max_quantity тощо)
        queryClient.invalidateQueries({ queryKey: ['realEstates'] });

        // Для Stars немає map_object у відповіді, тому робимо контрольний refetch
        // з повтором, доки plot не перетвориться на house
        (async () => {
          const maxAttempts = 6;
          const delayMs = 1000;
          for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            await queryClient.refetchQueries({ queryKey: ['mapObjects'] });
            const current = queryClient.getQueryData<MapObject[]>(['mapObjects']);
            const updated = current?.find((obj) => obj.id === plotId);
            if (updated?.type === 'house') {
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }

          setIsProcessing(false);
          // Показуємо попап — НЕ закриваємо модалку, юзер натисне "OK" і тоді все закриється
          setPurchasedRealEstateId(realEstateId);
          setShowPurchaseSuccessModal(true);
        })().catch((error) => {
          console.error('[RealEstateModal] Error refetching map objects after stars purchase:', error);
          setIsProcessing(false);
          onClose();
        });
      } else if (status === 'failed') {
        posthog.capture('real_estate_stars_payment_failed', {
          property_id: realEstateId,
          property_type: getRealEstateNameLocal(realEstateId),
          currency: 'stars',
          plot_id: plotId,
        });
        setIsProcessing(false);
        showAlert(t("Payment failed. Please try again."));
      } else {
        setIsProcessing(false);
      }
      // Якщо status === 'cancelled' - просто закриваємо без повідомлення
    });
  } catch (error) {
    console.error('[RealEstateModal] Invoice creation error:', error);

    if (handlePurchaseError(deps, error, realEstateId, 'stars', selectedEstateMeta)) {
      setIsProcessing(false);
    } else {
      posthog.capture('real_estate_purchase_error', {
        property_id: realEstateId,
        property_type: getRealEstateNameLocal(realEstateId),
        currency: 'stars',
        error_message: error instanceof Error ? error.message : 'invoice_creation_error',
        plot_id: plotId,
      });
      showAlert(t('Purchase failed. Please try again.'));
      setIsProcessing(false);
    }
  }
}
